import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  NavBar,
  SearchInput,
  UploadFileForm,
} from "../../../components";
import { useDate, useFetch, useRequest } from "../../../hooks";
import {
  ClientProps,
  DocumentProps,
  UserDataProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { DataGrid, GridColDef, GridDeleteIcon } from "@mui/x-data-grid";
import {
  Autocomplete,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import styles from "../../main.module.sass";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { useFilter } from "../../../hooks/useFilter";
import DataContext from "../../../context/DataContext";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../../utils/dataGridStyles";

interface Props extends UserDataProps {
  id: number;
}

interface FetchResponse {
  success?: boolean;
  data: Props[];
}

interface ChemicalProps {
  id: number;
  active: boolean;
  customName: string;
  date: string;
  idClient: number;
  idWell: number;
  path: string;
}

export default function ChemicalTrackerAdmin() {
  const theme = useTheme();
  const { data, isLoading } = useFetch<FetchResponse>("client");
  const { handleRequest } = useRequest();
  const { getDateFromString } = useDate();
  const [pullQuery, setPullQuery] = useState("");
  const [dataChemical, setdataChemical] = useState<ChemicalProps[]>([]);
  const { filteredData: chemicalData } = useFilter<ChemicalProps>(
    dataChemical,
    {
      query: pullQuery,
      field: "customName",
    },
  );
  const [idWellSelected, setIdWellSelected] = useState(null);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const matches = useMediaQuery("(min-width:600px)");
  const {
    clientOptions,
    wellOptions,
    fetchDataWells,
    fetchDataClient,
    onDeleteDocument,
  } = useContext(DataContext);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsError(false);
      setIsSuccess(false);
    }, 4000);
  }, [isError, isSuccess]);

  useEffect(() => {
    fetchDataClient();
  }, []);

  const fetchChemicalReport = (idWell: number) => {
    let options: RequestInit = {
      method: "GET",
    };
    handleRequest({ endpoint: `chemical-list/${idWell}`, options })
      .then((response) => {
        if (response.success) {
          // Ensure each row has a unique id for DataGrid
          const dataWithIds = response.data.map(
            (item: ChemicalProps, index: number) => ({
              ...item,
              id: item.id || index, // Use item.id if available, otherwise use index
            }),
          );
          setdataChemical(dataWithIds);
        } else {
          setIsError(true);
        }
      })
      .catch((e) => setIsError(true));
  };

  const fetchChemicalTrackerByKey = (
    key: string,
    name: string,
    download: boolean,
  ) => {
    if (key) {
      setIsCreatingReport(true);
      let options: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key }),
        headers: {
          responseType: "Buffer",
        },
      };
      handleRequest({ endpoint: "pdf-chemical-adm", options })
        .then((response) => {
          if (response.success) {
            createPdfFile(response.data, name, download);
          } else {
            setIsError(true);
          }
          setIsCreatingReport(false);
        })
        .catch((e) => {
          setIsError(true);
          setIsCreatingReport(false);
        });
    }
  };

  const createPdfFile = (
    data: { type: string; data: Buffer },
    name: string,
    download: boolean,
  ) => {
    try {
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(data.data).buffer], {
          type: "application/pdf;charset=utf-8",
        }),
      );
      if (download) {
        var link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
      } else {
        window.open(url);
      }
      setIsCreatingReport(false);
    } catch (e) {
      setIsCreatingReport(false);
      setIsError(true);
    }
  };

  const handleUploadChemical = async ({
    client,
    well,
    file,
    customName,
    installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      let formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      customName && formData.append("customName", customName);
      installationDate &&
        formData.append("installationDate", installationDate.toISOString());
      formData.append("type", "chemical-tracker");
      formData.append("file", file, file.name);
      const response = await fetch(
        process.env.REACT_APP_DEV_API + "/chemical-upload",
        {
          method: "POST",
          body: formData,
        },
      );
      const res = await response.json();
      if (res.success) {
        setIsCreatingReport(false);
        idWellSelected && fetchChemicalReport(idWellSelected);
        setIsSuccess(true);
      } else {
        setIsCreatingReport(false);
        setIsError(true);
      }
    } catch (e) {
      setIsCreatingReport(false);
      setIsError(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "customName",
      headerName: "Name",
      renderHeader: () => <strong>{"Chemical Tracker"}</strong>,
      sortable: true,
      width: 300,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.customName}>
            <span>{params.row.customName}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "date",
      headerName: "Date",
      renderHeader: () => <strong>{"Date"}</strong>,
      sortable: true,
      width: 200,
      renderCell: (params: any) => {
        return <span>{getDateFromString(params.row.date)}</span>;
      },
    },
    {
      field: "edit",
      headerName: "Actions",
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 180,
      renderCell: (params: any) => {
        return (
          <div className="action-buttons">
            <Tooltip title="Open in a new Page">
              <IconButton
                disabled={params.row.path.includes(".xlsx")}
                sx={modernIconButtonStyles(theme)}
                size="small"
                onClick={() =>
                  fetchChemicalTrackerByKey(
                    params.row.path,
                    params.row.customName,
                    false,
                  )
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                sx={modernIconButtonStyles(theme)}
                size="small"
                onClick={() =>
                  fetchChemicalTrackerByKey(
                    params.row.path,
                    params.row.customName,
                    true,
                  )
                }
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setIdToDelete(params.row?.id);
                  setIsOpenAlert(true);
                }}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <GridDeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
      disableColumnMenu: true,
    },
  ];

  const buttons = [
    {
      title: "Upload",
      action: () => setIsModalOpen(true),
      icon: <DriveFolderUploadIcon />,
      disabled: false,
    },
  ];

  const defaultProps = {
    options: clientOptions,
    getOptionLabel: (option: ClientProps) => option.name,
  };

  const onChangeClient = (clientSelected: ClientProps) => {
    fetchDataWells(clientSelected.id);
  };

  const defaultPropsWellSelector = {
    options: wellOptions,
    getOptionLabel: (option: WellProps) => option.name,
  };
  return (
    <>
      <NavBar title="Chemical Trackers" buttons={buttons} />
      <Grid
        container
        sx={{ marginTop: "2vh", marginLeft: "2vh" }}
        alignItems="center"
      >
        <Grid item xs={12} md={4} lg={4} justifyContent="center">
          <Autocomplete
            id="search-selector"
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            style={{ width: "80%", marginTop: "20px" }}
            onChange={(_, newValue) => newValue && onChangeClient(newValue)}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            {...defaultProps}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <Autocomplete
            id="well-selector"
            isOptionEqualToValue={(option, value) => {
              return true;
            }}
            style={{ width: "80%", marginTop: "20px" }}
            onChange={(_, newValue) => {
              if (newValue) {
                fetchChemicalReport(newValue.id as any);
                setIdWellSelected(newValue.id as any);
              }
            }}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              );
            }}
            {...defaultPropsWellSelector}
            disablePortal
            className={styles.formFieldModalCustom}
            renderInput={(params) => <TextField {...params} label="Well" />}
          />
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <SearchInput
            label="Search Chemical Tracker"
            value={pullQuery}
            setValue={setPullQuery}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={12} sx={{ marginTop: "2vh" }}>
          <DataGrid
            density={matches ? "standard" : "compact"}
            sx={{
              ...modernDataGridStyles(theme),
              width: "95%",
              maxWidth: "1200px",
              minHeight: 400,
            }}
            rows={chemicalData}
            columns={columns}
            loading={isLoading}
            autoPageSize
            pagination
            getRowId={(row) => row.id}
            rowHeight={60}
            slots={{
              noRowsOverlay: () => (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <>
                    Filter By Client and Well to see Chemical Trackers available
                  </>
                </div>
              ),
            }}
          />
        </Grid>
      </Grid>

      {isCreatingReport && <div className={styles.backdrop}></div>}
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isModalOpen && data && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={wellOptions}
              type="chemical-tracker"
              fetchDataWells={fetchDataWells}
              onUploadFile={(values: DocumentProps) => {
                handleUploadChemical(values);
              }}
              onCancel={() => {
                setIsModalOpen(false);
              }}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={
            <div>
              Are you sure you want to remove this Chemical Tracker from the
              Cloud?
            </div>
          }
          onAccept={() => {
            onDeleteDocument("chemical-file", idToDelete, () => {
              idWellSelected && fetchChemicalReport(idWellSelected);
            });
            setIdToDelete("");
            setIsOpenAlert(false);
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
}
